/*
  AHF Mega Menu — loaded (deferred) only by snippets/ahf-mega-menu.liquid.
*/
(() => {
  'use strict';

  if (customElements.get('ahf-mega-menu')) return;

  const DESKTOP = window.matchMedia('(min-width: 1201px)');
  const OPEN_DELAY = 60;
  const CLOSE_DELAY = 200;

  class AhfMegaMenu extends HTMLElement {
    connectedCallback() {
      this.panels = Array.from(this.querySelectorAll('.ahf-mm__panel'));
      this.entries = [];
      this.attached = false;
      this.mobileRoot = this.querySelector('.ahf-mm__mobile');
      this.mobileEntries = [];
      this.mobileAttached = false;

      this.onMediaChange = () => {
        if (DESKTOP.matches) {
          this.detachMobile();
          this.attach();
        } else {
          this.detach();
          this.attachMobile();
        }
      };
      DESKTOP.addEventListener('change', this.onMediaChange);
      if (DESKTOP.matches) {
        this.attach();
      } else {
        this.attachMobile();
      }

      if (window.Shopify && window.Shopify.designMode) {
        this.onBlockSelect = (event) => {
          if (!this.contains(event.target)) return;
          const panel = event.target.closest('.ahf-mm__panel');
          if (!panel) return;
          const entry = this.entries.find((en) => en.panel === panel);
          this.openPanel(entry || { panel: panel, trigger: null, details: null, li: null });
        };
        this.onBlockDeselect = (event) => {
          if (!this.contains(event.target)) return;
          this.closeAll();
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }
    }

    disconnectedCallback() {
      this.detach();
      this.detachMobile();
      DESKTOP.removeEventListener('change', this.onMediaChange);
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.onBlockDeselect) document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    /* ---------- attach / detach (desktop only) ---------- */

    attach() {
      if (this.attached) return;
      this.attached = true;

      const wrapper = this.closest('.header-wrapper');
      this.navRoot = wrapper ? wrapper.querySelector('.header__inline-menu') : null;

      const unmatched = [];
      if (this.navRoot) {
        // Existing markup (header-dropdown-menu.liquid): items with children are
        // li > header-menu > details > summary.header__menu-item, plain items are
        // li > a.header__menu-item; the visible title lives in a child <span>.
        const candidates = this.navRoot.querySelectorAll(
          'li > header-menu > details > summary.header__menu-item, li > a.header__menu-item'
        );
        const byTitle = new Map();
        candidates.forEach((el) => {
          const span = el.querySelector('span');
          const title = (span ? span.textContent : el.textContent).trim().toLowerCase();
          if (title && !byTitle.has(title)) byTitle.set(title, el);
        });

        this.panels.forEach((panel) => {
          const key = (panel.getAttribute('data-mm-trigger') || '').trim().toLowerCase();
          if (!key) {
            unmatched.push('(no menu item title set)');
            return;
          }
          const trigger = byTitle.get(key);
          if (!trigger) {
            unmatched.push('"' + key + '"');
            return;
          }
          const entry = {
            panel: panel,
            trigger: trigger,
            details: trigger.closest('details'),
            li: trigger.closest('li'),
            openTimer: 0,
            closeTimer: 0,
          };
          this.wire(entry);
          this.entries.push(entry);
        });
      } else {
        this.panels.forEach(() => unmatched.push('(built-in nav not found)'));
      }

      this.onDocClick = (event) => {
        this.entries.forEach((entry) => {
          if (entry.panel.hidden) return;
          if (entry.panel.contains(event.target)) return;
          if (entry.li && entry.li.contains(event.target)) return;
          this.closePanel(entry);
        });
      };
      document.addEventListener('click', this.onDocClick);

      this.onDocKeydown = (event) => {
        if (event.key !== 'Escape') return;
        const open = this.entries.find((entry) => !entry.panel.hidden);
        if (!open) return;
        this.closePanel(open);
        if (open.trigger) open.trigger.focus();
      };
      document.addEventListener('keydown', this.onDocKeydown);

      this.onResize = () => {
        this.entries.forEach((entry) => {
          if (!entry.panel.hidden) this.positionPanel(entry);
        });
      };
      window.addEventListener('resize', this.onResize);

      if (window.Shopify && window.Shopify.designMode && unmatched.length) {
        this.hintEl = document.createElement('p');
        this.hintEl.className = 'ahf-mm__hint';
        this.hintEl.textContent =
          'AHF Mega Menu (editor-only note): no top-level menu item matches panel title ' +
          unmatched.join(', ') +
          '. Set each panel’s "Menu item title" to the exact visible text of a menu item.';
        this.appendChild(this.hintEl);
      }
    }

    detach() {
      if (!this.attached) return;
      this.attached = false;

      this.entries.forEach((entry) => {
        window.clearTimeout(entry.openTimer);
        window.clearTimeout(entry.closeTimer);
        this.closePanel(entry);
        entry.trigger.removeEventListener('click', entry.onClickCapture, true);
        if (entry.details) entry.details.removeEventListener('toggle', entry.onNativeToggle);
        if (entry.li) {
          entry.li.removeEventListener('mouseenter', entry.onEnter);
          entry.li.removeEventListener('mouseleave', entry.onLeave);
        }
        entry.panel.removeEventListener('mouseenter', entry.onEnter);
        entry.panel.removeEventListener('mouseleave', entry.onLeave);
        entry.panel.removeEventListener('focusout', entry.onFocusOut);
        entry.trigger.removeAttribute('aria-controls');
        entry.trigger.removeAttribute('aria-haspopup');
        entry.trigger.removeAttribute('aria-expanded');
      });
      this.entries = [];

      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onDocKeydown);
      window.removeEventListener('resize', this.onResize);

      if (this.hintEl) {
        this.hintEl.remove();
        this.hintEl = null;
      }
    }

    /* ---------- mobile attach / detach (<=1200px, drawer) ---------- */

    attachMobile() {
      if (this.mobileAttached || !this.mobileRoot) return;
      this.mobileAttached = true;

      const wrapper = this.closest('.header-wrapper');
      const drawerMenu = wrapper ? wrapper.querySelector('.menu-drawer__menu') : null;
      if (!drawerMenu) return;

      // Existing drawer markup (header-drawer.liquid): items with children are
      // li > details.menu-accordion > summary; the visible title is the summary
      // text (its only other child is the caret svg, which adds no text).
      // Plain-link items have no accordion and keep native behavior.
      const summaries = drawerMenu.querySelectorAll('li > details.menu-accordion > summary');
      const byTitle = new Map();
      summaries.forEach((summary) => {
        const title = summary.textContent.trim().toLowerCase();
        if (title && !byTitle.has(title)) byTitle.set(title, summary);
      });

      const panels = Array.from(this.mobileRoot.querySelectorAll('.ahf-mm-m__panel'));
      panels.forEach((panel) => {
        const key = (panel.getAttribute('data-mm-trigger') || '').trim().toLowerCase();
        if (!key) return;
        const summary = byTitle.get(key);
        if (!summary) return;
        const details = summary.closest('details');
        if (!details) return;
        // Replace the item's native child links with the mega content while
        // attached (restored in detachMobile) — matched items only.
        const nativeList = details.querySelector(':scope > ul.menu-accordion__submenu');
        if (nativeList) nativeList.hidden = true;
        details.appendChild(panel);
        this.mobileEntries.push({ panel: panel, nativeList: nativeList });
      });
    }

    detachMobile() {
      if (!this.mobileAttached) return;
      this.mobileAttached = false;

      this.mobileEntries.forEach((entry) => {
        if (entry.nativeList) entry.nativeList.hidden = false;
        this.mobileRoot.appendChild(entry.panel);
      });
      this.mobileEntries = [];
    }

    /* ---------- per-entry wiring ---------- */

    wire(entry) {
      // Capture-phase interception: the native <details> never toggles and a
      // matched plain link never navigates; the mega panel opens instead.
      entry.onClickCapture = (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (entry.panel.hidden) {
          this.openPanel(entry);
        } else {
          this.closePanel(entry);
        }
      };
      entry.trigger.addEventListener('click', entry.onClickCapture, true);

      // Backstop: if anything else opens the native dropdown, close it and show
      // the mega panel instead.
      if (entry.details) {
        entry.onNativeToggle = () => {
          if (!entry.details.open) return;
          entry.details.open = false;
          this.openPanel(entry);
        };
        entry.details.addEventListener('toggle', entry.onNativeToggle);
      }

      // Hover-intent shared between the nav item and the panel, so moving the
      // cursor across the gap under the header does not close it.
      entry.onEnter = () => this.hoverIntent(entry, true);
      entry.onLeave = () => this.hoverIntent(entry, false);
      if (entry.li) {
        entry.li.addEventListener('mouseenter', entry.onEnter);
        entry.li.addEventListener('mouseleave', entry.onLeave);
      }
      entry.panel.addEventListener('mouseenter', entry.onEnter);
      entry.panel.addEventListener('mouseleave', entry.onLeave);

      entry.onFocusOut = () => {
        requestAnimationFrame(() => {
          const active = document.activeElement;
          if (entry.panel.contains(active) || active === entry.trigger) return;
          this.closePanel(entry);
        });
      };
      entry.panel.addEventListener('focusout', entry.onFocusOut);

      entry.trigger.setAttribute('aria-haspopup', 'true');
      entry.trigger.setAttribute('aria-controls', entry.panel.id);
      entry.trigger.setAttribute('aria-expanded', 'false');
    }

    hoverIntent(entry, entering) {
      window.clearTimeout(entry.openTimer);
      window.clearTimeout(entry.closeTimer);
      if (entering) {
        entry.openTimer = window.setTimeout(() => this.openPanel(entry), OPEN_DELAY);
      } else {
        entry.closeTimer = window.setTimeout(() => this.closePanel(entry), CLOSE_DELAY);
      }
    }

    /* ---------- open / close / position ---------- */

    openPanel(entry) {
      this.entries.forEach((other) => {
        if (other !== entry) this.closePanel(other);
      });
      // Never two dropdowns at once: also close any open native submenu.
      if (this.navRoot) {
        this.navRoot.querySelectorAll('details[open]').forEach((details) => {
          if (!entry.details || details !== entry.details) details.open = false;
        });
      }
      entry.panel.removeAttribute('hidden');
      this.positionPanel(entry);
      if (entry.trigger) entry.trigger.setAttribute('aria-expanded', 'true');
    }

    closePanel(entry) {
      if (entry.panel.hidden) return;
      entry.panel.setAttribute('hidden', '');
      if (entry.trigger) entry.trigger.setAttribute('aria-expanded', 'false');
    }

    closeAll() {
      this.entries.forEach((entry) => this.closePanel(entry));
      this.panels.forEach((panel) => {
        if (!panel.hidden) panel.setAttribute('hidden', '');
      });
    }

    positionPanel(entry) {
      if (!entry.panel.classList.contains('ahf-mm__panel--compact')) return;
      const wrapper = this.closest('.header-wrapper');
      if (!wrapper || !entry.trigger) return;
      const wrapperRect = wrapper.getBoundingClientRect();
      const triggerRect = entry.trigger.getBoundingClientRect();
      const panelWidth = entry.panel.offsetWidth || 300;
      let left = triggerRect.left - wrapperRect.left - 156;
      const maxLeft = wrapperRect.width - panelWidth - 8;
      if (left > maxLeft) left = maxLeft;
      if (left < 0) left = 0;
      entry.panel.style.setProperty('--mm-anchor-left', Math.round(left) + 'px');
    }
  }

  customElements.define('ahf-mega-menu', AhfMegaMenu);
})();
